import { FunctionComponent, useContext, useEffect, useState } from "react";
import { useRouter, NextRouter } from "next/router";
import Link from "next/link";
import { useTranslation } from "next-i18next/pages";
import { scroller } from "react-scroll";
import AppContext from "context/AppContext";
import styles from "../../styles/common/Navigation.module.css";

interface INavigationItem {
  name: string;
  path: string;
}

interface NavigationProps {
  isDarkColor: boolean;
  doAnimation?: boolean;
}

const Navigation: FunctionComponent<NavigationProps> = ({ isDarkColor, doAnimation = false }) => {
  const router: NextRouter = useRouter();
  const { isMobile } = useContext(AppContext);
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const [addNavStyle, setAddNavStyle] = useState<boolean>(false);
  const { t } = useTranslation("common");
  const mainNavigationItems: INavigationItem[] = t("navigation.all", { returnObjects: true }) as INavigationItem[];

  useEffect(() => {
    const handleScroll = (): void => setAddNavStyle(window.scrollY > 0);
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (showMenu) {
      document.body.classList.add("noScroll");
    } else {
      document.body.classList.remove("noScroll");
    }
  }, [showMenu]);

  const scrollTarget = (target: string) => {
    const scrollOffset: number = isMobile ? -100 : -120;
    return scroller.scrollTo(target, { smooth: true, offset: scrollOffset, duration: 500 });
  };

  const scrollToPage = async (target: string) => {
    if (router.pathname !== "/") {
      await router.push("/");
    }

    setShowMenu(false);
    scrollTarget(target);
  };

  const renderNavigationItem = (navigationItem: INavigationItem, index: number) => {
    if (navigationItem.path.startsWith("https")) {
      return (
        <a
          key={index}
          href={navigationItem.path}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${navigationItem.name} (opens in new tab)`}
        >
          {navigationItem.name}
        </a>
      );
    }

    return navigationItem.path.startsWith("/") ? (
      <Link key={index} href={navigationItem.path} onClick={() => setShowMenu(false)}>
        {navigationItem.name}
      </Link>
    ) : (
      <button
        key={index}
        className={styles.scrollButton}
        onClick={() => scrollToPage(navigationItem.path)}
        type="button"
      >
        {navigationItem.name}
      </button>
    );
  };

  return (
    <nav
      aria-label="Main navigation"
      className={`${styles.navbar} ${showMenu ? styles.show : ""}
                  ${isDarkColor ? styles.darkMode : ""} ${addNavStyle ? styles.backgroundStyle : ""}
                  ${doAnimation && !isMobile ? styles.doAnimation : ""}`}
    >
      <div className={styles.navInner}>
        <Link
          href="/"
          className={styles.brand}
          aria-label="Browserbite — Go to homepage"
          onClick={() => setShowMenu(false)}
        >
          <span
            className={`${styles.brandLogo} ${doAnimation && !isMobile ? styles.brandLogoAnimated : ""}`}
            aria-hidden="true"
          >
            <svg className={styles.logoMorph} viewBox="0 0 818 218" focusable="false" aria-hidden="true">
              <path className={styles.logoBracketLower} d="M0 27L0 218L203 218L203 200L18 200L18 27Z" />
              <path className={styles.logoBracketUpper} d="M818 0L818 191L800 191L800 18L536 18L536 0Z" />
              <path className={styles.logoDiamond} d="M632.8 59L624.4 67.4L632.8 75.7L641.1 67.4Z" />
            </svg>
            <span className={styles.logoWordmark} />
          </span>
        </Link>

        <div className={styles.navigationList} role="list">
          {mainNavigationItems.map((navigationItem: INavigationItem, index: number) => (
            <span key={`${navigationItem.name}-${index}`} className={styles.navigationItem} role="listitem">
              {renderNavigationItem(navigationItem, index)}
            </span>
          ))}

          <a
            href={t("global.calendlyLink")}
            className={`${styles.bookCallButton} ${styles.mobileBookCall}`}
            target="_blank"
            rel="noopener noreferrer"
            data-umami-event="booking-link-click"
            onClick={() => setShowMenu(false)}
          >
            {t("global.bookMeeting")} <span aria-hidden="true">↗</span>
          </a>
        </div>

        <div className={styles.actions}>
          <button
            className={styles.langSwitch}
            onClick={() => {
              const newLocale = router.locale === "de" ? "en" : "de";
              router.push(router.asPath, router.asPath, { locale: newLocale });
              setShowMenu(false);
            }}
            type="button"
            aria-label={router.locale === "de" ? "Switch to English" : "Zu Deutsch wechseln"}
          >
            <span className={router.locale === "en" ? styles.langActive : ""}>EN</span>
            <span aria-hidden="true">/</span>
            <span className={router.locale === "de" ? styles.langActive : ""}>DE</span>
          </button>

          <a
            href={t("global.calendlyLink")}
            className={styles.bookCallButton}
            target="_blank"
            rel="noopener noreferrer"
            data-umami-event="booking-link-click"
            onClick={() => setShowMenu(false)}
          >
            {t("global.bookMeeting")} <span aria-hidden="true">↗</span>
          </a>

          <button
            className={styles.mobileMenuIcon}
            onClick={() => setShowMenu((prevState: boolean) => !prevState)}
            aria-label={showMenu ? "Close menu" : "Open menu"}
            aria-expanded={showMenu}
            type="button"
          >
            <span />
            <span />
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
